/**
 * Profile (B-16): who this account is, and — the part that matters — every assumption
 * the score rests on.
 *
 * C-11 is the reason for the second half. A score is a single number produced from 161
 * bodyweight factors, a seconds-per-rep constant and a supported multiplier, none of
 * which the user chose. Showing them makes the number auditable instead of magical: a
 * score that looks wrong can be traced to the factor that produced it.
 *
 * The factor table used to render all 161 rows unconditionally, which made this page
 * 6,449px tall at 390px wide and gave it a column of "0.00" as its dominant content.
 * C-11 asks that the numbers be reachable and honest, not that they be unavoidable, so
 * the table is now opened by searching or by asking for it. Nothing was removed.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import type { BodyType, FitnessGoal, GymCosts, GymRef, Health, MuscleGroup, UserProfile, Workout } from "@altrtrack/core";
import {
  ACCOUNT_ROLES,
  BODY_TYPES,
  ADMIN_PHONE,
  CATALOG,
  CONDITIONS,
  FITNESS_GOALS,
  MUSCLE_GROUPS,
  SECONDS_PER_REP,
  SEXES,
  SUPPORTED_MULTIPLIER,
  formatNumber,
  isStaff,
  isValidGymCosts,
  normalise,
  planIsStale,
  plural,
  ptSessionCost,
  recordedConditions,
  sessionCost,
} from "@altrtrack/core";
import { useAuth } from "../auth.js";
import { localToday, useCatalogRevision, usePlan, useProfile, useStoredCatalogEdits, useWorkouts } from "../data.js";
import { ExerciseAdmin } from "../components/ExerciseAdmin.js";
import { saveGym, saveGymCosts, saveTrainingAnswers, setCurrentBodyweight } from "../write.js";
import { Choices, HealthQuestion } from "../components/Questions.js";
import { GymPicker } from "../components/GymPicker.js";
import { NumberField } from "../components/NumberField.js";
import { BODYWEIGHT, MONTHS, RUPEES, SESSIONS, checkNumber, type Bounds } from "../fields.js";
import { flagOf, formatNational, splitE164 } from "../phone.js";

const GROUP_NAMES: Record<MuscleGroup, string> = {
  chest: "Chest",
  back: "Back",
  shoulders: "Shoulders",
  biceps: "Biceps",
  triceps: "Triceps",
  forearms: "Forearms",
  quads: "Quads",
  hamstrings: "Hamstrings",
  glutes: "Glutes",
  calves: "Calves",
  core: "Core",
  fullBody: "Full body",
};

/**
 * The stored number, grouped for reading.
 *
 * B-20 left this as raw E.164 because grouping varies by country and guessing at it was
 * the wrong thing to get wrong inside a redesign. The country table from D-66 removes the
 * guess: the dial code is split off by longest match, so "+12461234567" is Barbados and
 * not a US number beginning "246". Anything that does not split — a number from a calling
 * code the table does not carry — is shown exactly as stored rather than mangled.
 */
function readableNumber(e164: string): string {
  const parts = splitE164(e164);
  if (!parts) return e164;
  const { country, national } = parts;
  return `${flagOf(country.iso)} +${country.dial} ${formatNational(national, country.lengths?.[0])}`;
}

export function ProfilePage({ uid }: { uid: string }) {
  const { signOut, user } = useAuth();
  const catalogEdits = useStoredCatalogEdits();
  // Presentation only: firestore.rules refuses every catalog write from any other number.
  const isAdmin = user?.phoneNumber === ADMIN_PHONE;
  const profile = useProfile(uid);
  const workouts = useWorkouts(uid);
  const [search, setSearch] = useState("");
  const [group, setGroup] = useState<MuscleGroup | "">("");
  const [showAll, setShowAll] = useState(false);
  const [weight, setWeight] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const revision = useCatalogRevision();
  const needle = normalise(search.trim());
  const matches = useMemo(
    () =>
      CATALOG.filter(
        (e) =>
          (!needle || normalise(e.name).includes(needle)) &&
          (!group || e.muscleGroup === group),
      ),
    // `revision` stands for CATALOG, which is read by reference (B-66).
    [needle, group, revision],
  );

  if (profile.loading) return <p className="sub">Loading…</p>;
  if (!profile.data) return <p className="sub">No profile found for this account.</p>;

  const me = profile.data;
  // `NumberField` only ever hands back a value that passed `checkNumber`, so this is
  // either the saved weight or a valid new one — never the half-typed string the old
  // `draftWeight` held, which is what made "changed" have to re-validate here.
  const current = weight ?? me.currentBodyweightKg;
  const changed = current !== me.currentBodyweightKg;
  const days = workouts.data?.length ?? 0;
  // Either control opens the table on its own; the toggle is for browsing with neither.
  const open = needle.length > 0 || group !== "" || showAll;

  const save = async () => {
    setSaving(true);
    await setCurrentBodyweight(uid, current);
    setSaving(false);
    setWeight(null);
  };

  return (
    <div className="profile">
      <header className="pcard screen-head">
        <p className="label">Signed in as</p>
        <h1 className="title">{readableNumber(me.phoneNumber)}</h1>
        <p className="sub">
          {me.timezone} · {days} gym {plural(days, "day")} logged
        </p>
      </header>

      <AboutYou me={me} />

      {(!me.role || !isStaff(me.role)) && <TrainingCard uid={uid} me={me} />}
      {(!me.role || !isStaff(me.role)) && <GymCard uid={uid} me={me} workouts={workouts.data} />}

      <section className="pcard">
        <h2 className="label pcard-label">Bodyweight</h2>
        <div className="field-row">
          <label className="field">
            <span className="label">Current (kg)</span>
            <NumberField
              className="input input-narrow"
              value={current}
              bounds={BODYWEIGHT}
              label="Current bodyweight, in kilograms"
              onCommit={setWeight}
            />
          </label>
          <button
            className="btn btn-primary"
            type="button"
            disabled={!changed || saving}
            onClick={save}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        <p className="sub">
          This applies to days logged from now on. Every past day keeps the weight it was
          logged at, so an old score never moves. To change one day, edit that day.
        </p>
      </section>

      {/*
        The two reference cards are closed until tapped (D-96). They answer "why is my
        score this number?", which is asked rarely and never mid-session, and open they
        were most of this page's height.
      */}
      <Fold title="How a score is worked out" hint="The formula, and what it assumes">
        <p className="formula">(bodyweight × factor + added weight) × reps</p>
        <dl className="constants">
          <dt>A timed exercise counts</dt>
          <dd>{SECONDS_PER_REP}s = 1 rep</dd>
          <dt>A supported set scores</dt>
          <dd>{SUPPORTED_MULTIPLIER * 100}%</dd>
          <dt>
            At {me.currentBodyweightKg}kg, factor 0.65 × 10 reps
          </dt>
          <dd>{formatNumber(me.currentBodyweightKg * 0.65 * 10)}</dd>
        </dl>
        <p className="sub">
          The factor is the fraction of your bodyweight the movement actually lifts. It is
          0 for machine and free-weight work, where only the added weight counts. A
          supported set does not stack with an assisted variant, which already carries a
          halved factor.
        </p>
      </Fold>

      {isAdmin && (
        <Fold title="Exercise list · admin" hint="Add an exercise, or rename one">
          <ExerciseAdmin uid={uid} edits={catalogEdits} />
        </Fold>
      )}

      <Fold title="Bodyweight factors" hint={`All ${CATALOG.length} exercises, searchable`}>
        <div className="factors-head">
          <input
            className="input"
            type="search"
            value={search}
            placeholder={`Search ${CATALOG.length} exercises`}
            aria-label="Find an exercise in the factor table"
            onChange={(e) => setSearch(e.target.value)}
          />
          {!needle && group === "" && (
            <button
              className="btn"
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((v) => !v)}
            >
              {showAll ? "Hide" : "Show all"}
            </button>
          )}
          {/*
            * A second way into the rows, for the times you do not have a name in mind.
            * Search answers "what is the factor for a hack squat"; this answers "what
            * does it assume about legs", which is the question C-11 is really about.
            */}
          <select
            className="select factors-filter"
            aria-label="Show one muscle group only"
            value={group}
            onChange={(e) => setGroup(e.target.value as MuscleGroup | "")}
          >
            <option value="">All muscle groups</option>
            {MUSCLE_GROUPS.map((g) => (
              <option key={g} value={g}>
                {GROUP_NAMES[g]}
              </option>
            ))}
          </select>
        </div>

        {!open && (
          <p className="sub factors-empty">
            Search for a movement to check the factor it was scored with.
          </p>
        )}

        {open && matches.length === 0 && (
          <p className="sub factors-empty">
            Nothing matches{search.trim() && ` “${search.trim()}”`}
            {search.trim() && group && " in"}
            {group && ` ${GROUP_NAMES[group]}`}.
          </p>
        )}

        {open &&
          MUSCLE_GROUPS.map((group) => {
            const rows = matches.filter((e) => e.muscleGroup === group);
            if (rows.length === 0) return null;
            return (
              <section className="factor-group" key={group}>
                <h3 className="factor-group-name">{GROUP_NAMES[group]}</h3>
                <ul className="factor-list">
                  {rows.map((e) => (
                    <li className="factor-row" key={e.id}>
                      <span>
                        {e.name}
                        {e.measure === "duration" && (
                          <span className="factor-unit">seconds</span>
                        )}
                      </span>
                      <span
                        className={`factor-value${e.bodyweightFactor === 0 ? " zero" : ""}`}
                      >
                        {e.bodyweightFactor.toFixed(2)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
      </Fold>

      {/*
        Sign out lives here rather than in the masthead (D-82). It is not navigation, and
        the bottom of the screen someone opens to check who they are signed in as is where
        they look to stop being signed in as them.

        A plain button, and no confirmation. Signing out destroys nothing — a draft in the
        editor is the only unsaved state anywhere, and this button is on a different screen
        from it — so `--fault`, which means destructive or failed, would be wrong here.
      */}
      <section className="pcard signout">
        <button className="btn" type="button" onClick={signOut}>
          Sign out
        </button>
        <p className="sub">
          Signs out {readableNumber(me.phoneNumber)} on this device. Your log stays where it
          is, and the bot keeps logging.
        </p>
      </section>
    </div>
  );
}

/**
 * A card that opens on tap (D-96). The whole heading row is the button, so the target is
 * the card's width rather than a chevron. Closed, the body is not rendered at all — the
 * factor table's search state lives in the page, so closing and reopening keeps it.
 */
function Fold({ title, hint, children }: { title: string; hint: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="pcard fold">
      <h2 className="fold-heading">
        <button
          className="fold-head"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className="fold-title">{title}</span>
          <span className="sub fold-hint">{hint}</span>
          <span className="fold-chevron" aria-hidden="true" />
        </button>
      </h2>
      {open && <div className="fold-body">{children}</div>}
    </section>
  );
}

/**
 * The answers given at sign-up (B-39), read back.
 *
 * Read-only on purpose, for now. Bodyweight is the one of them the app actually computes
 * with, and it already has an editor directly below this — the rest are recorded facts
 * that nothing yet reads, so an editor for them would be a control with no consequence.
 *
 * The labels come from the same lists the onboarding screen asked from, never from a
 * second spelling here: a goal renamed in `packages/core/src/onboarding.ts` has to change
 * in both places at once or it changes in neither (D-09).
 */
function AboutYou({ me }: { me: UserProfile }) {
  // Nothing to show for an account that predates onboarding, so nothing is rendered —
  // an empty panel of dashes says less than no panel at all.
  if (!me.role) return null;

  const role = ACCOUNT_ROLES.find((r) => r.id === me.role);
  const sex = SEXES.find((s) => s.id === me.sex);

  /*
   * The age is subtracted here rather than stored, which is the whole reason `birthYear`
   * is what gets written (D-90). It uses the browser's year: this is a fact about the
   * person and not about a workout, so it does not need the profile's timezone the way a
   * logged day does.
   */
  const age = me.birthYear === undefined ? null : new Date().getFullYear() - me.birthYear;

  return (
    <section className="pcard">
      <h2 className="label pcard-label">About you</h2>
      <dl className="constants about">
        <dt>Account</dt>
        <dd>{role?.label ?? me.role}</dd>

        {me.role && isStaff(me.role) ? (
          <>
            {me.name && (
              <>
                <dt>Name</dt>
                <dd>{me.name}</dd>
              </>
            )}
            {(me.gym?.name ?? me.gymName) && (
              <>
                <dt>Gym</dt>
                <dd>{me.gym?.name ?? me.gymName}</dd>
              </>
            )}
            {(me.gym?.pinCode ?? me.areaPinCode) && (
              <>
                <dt>Area PIN code</dt>
                <dd>{me.gym?.pinCode ?? me.areaPinCode}</dd>
              </>
            )}
            {me.gymMapsUrl && (
              <>
                <dt>Map</dt>
                {/* The stored URL is long enough to wrap a right-aligned column into
                    three lines, and nobody reads one. It is the link instead. */}
                <dd>
                  <a href={me.gymMapsUrl} target="_blank" rel="noreferrer noopener">
                    Open in Maps
                  </a>
                </dd>
              </>
            )}
          </>
        ) : (
          <>
            {sex && (
              <>
                <dt>Sex</dt>
                <dd>{sex.label}</dd>
              </>
            )}
            {age !== null && (
              <>
                <dt>Age</dt>
                <dd>{age}</dd>
              </>
            )}
          </>
        )}
      </dl>
    </section>
  );
}

type Editing = "bodyType" | "goals" | "health" | null;

const PLAN_WEEK = [1, 2, 3, 4, 5, 6, 0] as const;
const PLAN_DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/**
 * What a plan would be built from (B-44): build, goals, injuries — each "Not answered"
 * until it is, and each editable here, one at a time.
 *
 * Goals moved here from About you: it is a plan input now, and one answer shown in two
 * cards is one answer with two places to look for it.
 *
 * The editor holds a draft and writes only on Save, unlike the onboarding flow's single
 * write at the end — here each answer stands alone, so there is nothing to batch it with.
 */
function TrainingCard({ uid, me }: { uid: string; me: UserProfile }) {
  const plan = usePlan(uid);
  const [editing, setEditing] = useState<Editing>(null);
  const [bodyType, setBodyType] = useState<BodyType | undefined>(undefined);
  const [goals, setGoals] = useState<readonly FitnessGoal[]>([]);
  const [health, setHealth] = useState<Health | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const open = (which: Exclude<Editing, null>) => {
    setBodyType(me.bodyType);
    setGoals(me.goals ?? []);
    setHealth(me.health);
    setFailure(null);
    setEditing(which);
  };

  const canSave =
    editing === "bodyType"
      ? bodyType !== undefined
      : editing === "goals"
        ? goals.length > 0
        : health !== undefined && ("none" in health || health.conditions.length > 0);

  const save = async () => {
    setSaving(true);
    const patch =
      editing === "bodyType" ? { bodyType } : editing === "goals" ? { goals } : { health };
    const result = await saveTrainingAnswers(uid, patch as Pick<UserProfile, "bodyType" | "goals" | "health">);
    setSaving(false);
    if (result.ok) setEditing(null);
    else setFailure(result.reason === "failed" ? result.detail : "That didn't save.");
  };

  const build = BODY_TYPES.find((b) => b.id === me.bodyType)?.label;
  const goalLabels = (me.goals ?? [])
    .map((id) => FITNESS_GOALS.find((g) => g.id === id)?.label)
    .filter((label): label is string => label !== undefined);
  const injuries =
    me.health === undefined
      ? undefined
      : "none" in me.health
        ? "None"
        : recordedConditions(me.health)
            .map((c) => CONDITIONS.find((o) => o.id === c.id)?.label ?? c.id)
            .join(", ");

  const rows: { key: Exclude<Editing, null>; title: string; value: string | undefined }[] = [
    { key: "bodyType", title: "Build", value: build },
    { key: "goals", title: "Training for", value: goalLabels.length > 0 ? goalLabels.join(", ") : undefined },
    { key: "health", title: "Injuries", value: injuries },
  ];

  return (
    <section className="pcard">
      <h2 className="label pcard-label">Training plan</h2>

      {editing === null ? (
        <>
          <ul className="answers">
            {rows.map((row) => (
              <li key={row.key}>
                <button className="answer-row" type="button" onClick={() => open(row.key)}>
                  <span className="label">{row.title}</span>
                  <span className={row.value ? "answer-value" : "answer-value sub"}>
                    {row.value ?? "Not answered"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {plan.data ? (
            <>
              <ul className="plan-week">
                {PLAN_WEEK.filter((d) => plan.data!.days[d]).map((d) => (
                  <li key={d}>
                    <span>{PLAN_DAY_NAMES[d]}</span>
                    <span className="sub">{plan.data!.days[d]!.label}</span>
                  </li>
                ))}
              </ul>
              {planIsStale(plan.data, me) && (
                <p className="sub">Your answers changed since this plan was made.</p>
              )}
              <p className="training-actions">
                <Link className="btn btn-primary" to="/plan">
                  View plan
                </Link>
                <Link className="btn" to="/plan/new">
                  Regenerate
                </Link>
              </p>
            </>
          ) : (
            <>
              {plan.error === "unreadable" && <p className="sub">Your saved plan couldn't be read.</p>}
              <p className="training-actions">
                <Link className="btn btn-primary" to="/plan/new">
                  Create my plan
                </Link>
              </p>
            </>
          )}
        </>
      ) : (
        <>
          {editing === "bodyType" && (
            <Choices options={BODY_TYPES} selected={bodyType} onPick={setBodyType} />
          )}
          {editing === "goals" && (
            <Choices
              options={FITNESS_GOALS}
              selected={goals}
              onPick={(goal) =>
                setGoals((current) =>
                  current.includes(goal) ? current.filter((g) => g !== goal) : [...current, goal],
                )
              }
            />
          )}
          {editing === "health" && (
            <HealthQuestion value={health} onChange={(update) => setHealth((current) => update(current))} />
          )}
          {failure && (
            <div className="notice bad">
              <b>That didn't save.</b>
              <p className="sub">{failure}</p>
            </div>
          )}
          <div className="field-row">
            <button className="btn" type="button" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </button>
            <button className="btn btn-primary" type="button" onClick={() => void save()} disabled={!canSave || saving}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** ₹ with Indian digit grouping — ₹1,00,000. The dashboard owns Intl; core never does. */
function rupees(amount: number): string {
  return `₹${new Intl.NumberFormat("en-IN").format(amount)}`;
}

/**
 * A whole number that may be empty. Not `NumberField`, for the reason onboarding's
 * `Answer` gives: an amount nobody has entered is not ₹0, and prefilling one would be a
 * plausible lie a single tap on Save would store.
 */
function CostField({
  label,
  bounds,
  draft,
  onDraft,
}: {
  label: string;
  bounds: Bounds;
  draft: string;
  onDraft: (next: string) => void;
}) {
  const problem = draft.trim() === "" ? null : checkNumber(draft, bounds).message;
  return (
    <label className="field">
      <span className="label">{label}</span>
      <input
        className="input input-narrow"
        value={draft}
        inputMode="numeric"
        aria-invalid={problem !== null}
        onChange={(e) => onDraft(e.target.value)}
      />
      {problem && <span className="field-error">{problem}</span>}
    </label>
  );
}

const asDraft = (n: number | undefined) => (n === undefined ? "" : String(n));

/**
 * Your gym, what it costs, and what a session costs (B-52, D-104, D-105). Everything
 * here is on the user's own document, which no one else can read.
 *
 * `workouts` is null while its subscription loads, so the card can say nothing about
 * sessions rather than "No sessions yet this month" for a month that has some.
 */
function GymCard({ uid, me, workouts }: { uid: string; me: UserProfile; workouts: readonly Workout[] | null }) {
  const [picking, setPicking] = useState(false);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState("1");
  const [takesPt, setTakesPt] = useState(false);
  const [ptAmount, setPtAmount] = useState("");
  const [ptSessions, setPtSessions] = useState("");
  const [ptMonths, setPtMonths] = useState("1");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  // A stored value core would refuse is treated as never entered: "Add what you pay",
  // never ₹NaN or ₹Infinity, and the editor starts empty rather than from it.
  const costs = isValidGymCosts(me.gymCosts) ? me.gymCosts : undefined;

  const openEditor = () => {
    const c = costs;
    setAmount(asDraft(c?.membership.amountInr));
    setMonths(asDraft(c?.membership.months ?? 1));
    setTakesPt(c?.pt !== undefined);
    setPtAmount(asDraft(c?.pt?.amountInr));
    setPtSessions(asDraft(c?.pt?.sessions));
    setPtMonths(asDraft(c?.pt?.months ?? 1));
    setFailure(null);
    setEditing(true);
  };

  const num = (draft: string, bounds: Bounds) => checkNumber(draft, bounds).value;
  const draftCosts: GymCosts | null = (() => {
    const a = num(amount, RUPEES);
    const m = num(months, MONTHS);
    if (a === null || m === null) return null;
    if (!takesPt) return { membership: { amountInr: a, months: m } };
    const pa = num(ptAmount, RUPEES);
    const ps = num(ptSessions, SESSIONS);
    const pm = num(ptMonths, MONTHS);
    if (pa === null || ps === null || pm === null) return null;
    return { membership: { amountInr: a, months: m }, pt: { amountInr: pa, sessions: ps, months: pm } };
  })();

  const pick = async (gym: GymRef) => {
    setFailure(null);
    const result = await saveGym(uid, gym);
    if (result.ok) setPicking(false);
    else setFailure(result.reason === "failed" ? result.detail : "That didn't save.");
  };

  const save = async () => {
    if (!draftCosts) return;
    setFailure(null);
    setSaving(true);
    const result = await saveGymCosts(uid, draftCosts);
    setSaving(false);
    if (result.ok) setEditing(false);
    else setFailure(result.reason === "failed" ? result.detail : "That didn't save.");
  };

  const today = localToday(me.timezone);
  const monthName = MONTH_NAMES[Number(today.slice(5, 7)) - 1];
  const summary = costs ? sessionCost(costs, workouts ?? [], today) : null;
  const ptEach = costs ? ptSessionCost(costs) : null;

  return (
    <section className="pcard">
      <h2 className="label pcard-label">Your gym</h2>

      {picking || !me.gym ? (
        <>
          <GymPicker uid={uid} onPick={pick} initialPin={me.gym?.pinCode ?? ""} selected={me.gym} />
          {me.gym && (
            <button
              className="btn"
              type="button"
              onClick={() => {
                setFailure(null);
                setPicking(false);
              }}
            >
              Cancel
            </button>
          )}
        </>
      ) : (
        <div className="gym-current">
          <p>
            {me.gym.name} <span className="sub">· {me.gym.pinCode}</span>
          </p>
          <button
            className="btn"
            type="button"
            onClick={() => {
              setFailure(null);
              setPicking(true);
            }}
          >
            Change
          </button>
        </div>
      )}

      {editing ? (
        <div className="gym-costs">
          <div className="field-row">
            <CostField label="Membership (₹)" bounds={RUPEES} draft={amount} onDraft={setAmount} />
            <CostField label="Covers months" bounds={MONTHS} draft={months} onDraft={setMonths} />
          </div>
          <label className="check">
            <input type="checkbox" checked={takesPt} onChange={(e) => setTakesPt(e.target.checked)} />
            <span>I take personal training</span>
          </label>
          {takesPt && (
            <div className="field-row">
              <CostField label="PT pack (₹)" bounds={RUPEES} draft={ptAmount} onDraft={setPtAmount} />
              <CostField label="Sessions" bounds={SESSIONS} draft={ptSessions} onDraft={setPtSessions} />
              <CostField label="Over months" bounds={MONTHS} draft={ptMonths} onDraft={setPtMonths} />
            </div>
          )}
          <p className="training-actions">
            <button className="btn btn-primary" type="button" disabled={!draftCosts || saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => {
                setFailure(null);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </p>
        </div>
      ) : summary ? (
        <>
          <p className="gym-summary">
            <b>{rupees(summary.spend)} a month</b>
            {workouts !== null && (
              <>
                {" · "}
                {summary.perSession === null ? (
                  "No sessions yet this month"
                ) : (
                  <>
                    {summary.sessions} {plural(summary.sessions, "session")} so far in {monthName}
                    {" · "}
                    {costs?.pt ? (
                      <>
                        <b>{rupees(summary.perSession)} a normal day</b>
                        {" · "}
                        {summary.perPtSession === null ? (
                          "No PT sessions marked this month"
                        ) : (
                          <b>{rupees(summary.perPtSession)} a PT day</b>
                        )}
                      </>
                    ) : (
                      <b>{rupees(summary.perSession)} a session</b>
                    )}
                  </>
                )}
              </>
            )}
          </p>
          {ptEach !== null && <p className="sub">Personal training works out to {rupees(ptEach)} a session.</p>}
          <p className="training-actions">
            <button className="btn" type="button" onClick={openEditor}>
              Edit what you pay
            </button>
          </p>
        </>
      ) : (
        <p className="training-actions">
          <button className="btn btn-primary" type="button" onClick={openEditor}>
            Add what you pay
          </button>
        </p>
      )}

      {failure && (
        <div className="notice bad">
          <b>That didn't save.</b>
          <p className="sub">{failure}</p>
        </div>
      )}
      <p className="sub">Only you can see what you pay.</p>
    </section>
  );
}
